import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Briefcase, X, TrendingUp, TrendingDown } from 'lucide-react';

import {
  createPortfolioThunk,
  fetchPortfoliosThunk,
  deletePortfolioThunk,
  bulkCreateStocksThunk,
  fetchStocksByPortfolioIdThunk,
  deleteStockThunk,
} from '../../redux/portfolio/portfolioActionThunks';
import { fetchBatchSnapshots } from '../../redux/stockData/marketSnapshot/marketSnapshotActions';
import StockLogo from '../../components/StockLogo/StockLogoRender';

const formatMoney = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
};

const CreatePortfolioForm = ({ onCancel, onCreate, isSubmitting }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onCreate({ name: name.trim(), description: description.trim(), currency: 'USD' });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('portfolio.createForm.namePlaceholder')}
        maxLength={255}
        className="w-full bg-white dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700/50 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t('portfolio.createForm.descriptionPlaceholder')}
        maxLength={1000}
        rows={2}
        className="w-full bg-white dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700/50 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
          {t('portfolio.cancel')}
        </button>
        <button
          type="submit"
          disabled={!name.trim() || isSubmitting}
          className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg"
        >
          {isSubmitting ? t('portfolio.createForm.creating') : t('portfolio.createForm.submit')}
        </button>
      </div>
    </form>
  );
};

const AddHoldingForm = ({ onCancel, onAdd, isSubmitting }) => {
  const { t } = useTranslation();
  const [ticker, setTicker] = useState('');
  const [quantity, setQuantity] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().slice(0, 10));

  const canSubmit = ticker.trim() && Number(quantity) > 0 && Number(purchasePrice) >= 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onAdd({
      ticker: ticker.trim().toUpperCase(),
      quantity: Number(quantity),
      purchase_price: Number(purchasePrice),
      purchase_date: new Date(purchaseDate).toISOString(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <input
        type="text"
        value={ticker}
        onChange={(e) => setTicker(e.target.value)}
        placeholder={t('portfolio.addHoldingForm.tickerPlaceholder')}
        maxLength={10}
        className="bg-white dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700/50 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <input
        type="number"
        min="0"
        step="any"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        placeholder={t('portfolio.addHoldingForm.sharesPlaceholder')}
        className="bg-white dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700/50 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <input
        type="number"
        min="0"
        step="any"
        value={purchasePrice}
        onChange={(e) => setPurchasePrice(e.target.value)}
        placeholder={t('portfolio.addHoldingForm.costPerSharePlaceholder')}
        className="bg-white dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700/50 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <input
        type="date"
        value={purchaseDate}
        onChange={(e) => setPurchaseDate(e.target.value)}
        className="bg-white dark:bg-gray-900/60 border border-gray-300 dark:border-gray-700/50 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="col-span-2 sm:col-span-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
          {t('portfolio.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit || isSubmitting}
          className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg"
        >
          {isSubmitting ? t('portfolio.addHoldingForm.adding') : t('portfolio.addHoldingForm.submit')}
        </button>
      </div>
    </form>
  );
};

const PortfolioPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const portfolios = useSelector((s) => s.portfolios?.portfolios || []);
  const stocks = useSelector((s) => s.portfolios?.stocks || []);
  const loading = useSelector((s) => s.portfolios?.loading || {});
  const error = useSelector((s) => s.portfolios?.error);
  const batchSnapshots = useSelector((s) => s.marketSnapshot?.batchSnapshots || []);

  const [selectedId, setSelectedId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddHolding, setShowAddHolding] = useState(false);

  useEffect(() => {
    if (portfolios.length === 0) dispatch(fetchPortfoliosThunk());
  }, [dispatch, portfolios.length]);

  useEffect(() => {
    if (selectedId) {
      dispatch(fetchStocksByPortfolioIdThunk(selectedId));
    }
  }, [selectedId, dispatch]);

  const selectedStocks = useMemo(
    () => stocks.filter((s) => s.portfolio_id === selectedId),
    [stocks, selectedId]
  );

  const tickers = useMemo(
    () => Array.from(new Set(selectedStocks.map((s) => s.ticker))),
    [selectedStocks]
  );

  useEffect(() => {
    if (tickers.length > 0) {
      dispatch(fetchBatchSnapshots(tickers));
    }
  }, [tickers, dispatch]);

  const priceByTicker = useMemo(() => {
    const map = {};
    for (const snap of batchSnapshots) {
      map[snap.ticker] = snap.last_price;
    }
    return map;
  }, [batchSnapshots]);

  const summary = useMemo(() => {
    let costBasis = 0;
    let currentValue = 0;
    let pricedHoldings = 0;
    for (const holding of selectedStocks) {
      const qty = Number(holding.quantity) || 0;
      const cost = Number(holding.purchase_price) || 0;
      costBasis += qty * cost;
      const livePrice = priceByTicker[holding.ticker];
      if (livePrice != null) {
        currentValue += qty * livePrice;
        pricedHoldings += 1;
      } else {
        currentValue += qty * cost; // fall back to cost basis if no live price yet
      }
    }
    const gainLoss = currentValue - costBasis;
    const gainLossPct = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0;
    return { costBasis, currentValue, gainLoss, gainLossPct, allPriced: pricedHoldings === selectedStocks.length };
  }, [selectedStocks, priceByTicker]);

  const handleCreatePortfolio = useCallback(
    async (portfolioData) => {
      // user_id is required by the request schema but always overridden
      // server-side from the authenticated session -- see crud_portfolio.py.
      const result = await dispatch(createPortfolioThunk({ ...portfolioData, user_id: 0 }));
      if (result.meta.requestStatus === 'fulfilled') {
        setShowCreateForm(false);
        setSelectedId(result.payload.id);
      }
    },
    [dispatch]
  );

  const handleDeletePortfolio = useCallback(
    async (id) => {
      if (!window.confirm(t('portfolio.deleteConfirm'))) return;
      const result = await dispatch(deletePortfolioThunk(id));
      if (result.meta.requestStatus === 'fulfilled' && selectedId === id) {
        setSelectedId(null);
      }
    },
    [dispatch, selectedId, t]
  );

  const handleAddHolding = useCallback(
    async (holding) => {
      const result = await dispatch(
        bulkCreateStocksThunk({ portfolioId: selectedId, stocks: [{ ...holding, portfolio_id: selectedId }] })
      );
      if (result.meta.requestStatus === 'fulfilled') {
        setShowAddHolding(false);
      }
    },
    [dispatch, selectedId]
  );

  const handleDeleteHolding = useCallback(
    (stockId) => {
      dispatch(deleteStockThunk({ portfolioId: selectedId, stockId }));
    },
    [dispatch, selectedId]
  );

  const selectedPortfolio = portfolios.find((p) => p.id === selectedId);

  return (
    <div className="relative w-full min-h-screen">
      <div className="w-full bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/50 overflow-x-hidden">
        <div className="w-full">
          <div className="max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6 text-gray-900 dark:text-slate-100 w-full space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('portfolio.title')}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolio.subtitle')}</p>
              </div>
              {!showCreateForm && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
                >
                  <Plus size={16} />
                  {t('portfolio.newPortfolio')}
                </button>
              )}
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-600/40 rounded-xl p-4 text-sm text-red-700 dark:text-red-300">
                {typeof error === 'string' ? error : t('common.somethingWentWrongPeriod')}
              </div>
            )}

            {showCreateForm && (
              <CreatePortfolioForm
                onCancel={() => setShowCreateForm(false)}
                onCreate={handleCreatePortfolio}
                isSubmitting={loading.portfolios}
              />
            )}

            {/* Portfolio list */}
            {portfolios.length === 0 && !loading.portfolios && !showCreateForm && (
              <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-gray-200 dark:border-gray-700/50">
                <Briefcase className="mx-auto text-gray-400 dark:text-gray-500 mb-4" size={40} />
                <p className="text-gray-600 dark:text-gray-400">{t('portfolio.emptyState.title')}</p>
                <p className="text-gray-500 text-sm mt-1">{t('portfolio.emptyState.subtitle')}</p>
              </div>
            )}

            {portfolios.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {portfolios.map((p) => (
                  // Select and delete are two *sibling* interactive
                  // controls, not one nested inside the other -- axe's
                  // nested-interactive rule flags an interactive descendant
                  // even under an ARIA role="button" ancestor, not just a
                  // real <button>, so the earlier role="button" wrapper
                  // (still containing the delete button) didn't actually
                  // fix this. The card itself is a plain, non-interactive
                  // <div>; the portfolio name is its own real <button>.
                  <div
                    key={p.id}
                    className={`bg-gray-50 dark:bg-gray-800/60 border rounded-xl p-4 transition-colors ${
                      selectedId === p.id ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300 dark:border-gray-700/50 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedId(p.id)}
                        className="flex-1 min-w-0 text-left font-semibold text-gray-900 dark:text-white truncate"
                      >
                        {p.name}
                      </button>
                      <button
                        type="button"
                        aria-label={t('portfolio.deletePortfolioAriaLabel', { name: p.name })}
                        onClick={() => handleDeletePortfolio(p.id)}
                        className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 flex-shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    {p.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{p.description}</p>}
                    <p className="text-xs text-gray-500 mt-2">{p.currency}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Selected portfolio detail */}
            {selectedPortfolio && (
              <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-2xl p-5 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedPortfolio.name}</h2>
                  {!showAddHolding && (
                    <button
                      onClick={() => setShowAddHolding(true)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <Plus size={14} /> {t('portfolio.addHolding')}
                    </button>
                  )}
                </div>

                {showAddHolding && (
                  <AddHoldingForm
                    onCancel={() => setShowAddHolding(false)}
                    onAdd={handleAddHolding}
                    isSubmitting={loading.stocks}
                  />
                )}

                {selectedStocks.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-white dark:bg-gray-900/50 rounded-xl p-3">
                      <p className="text-xs text-gray-500">{t('portfolio.summary.costBasis')}</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{formatMoney(summary.costBasis)}</p>
                    </div>
                    <div className="bg-white dark:bg-gray-900/50 rounded-xl p-3">
                      <p className="text-xs text-gray-500">{t('portfolio.summary.currentValue')}{!summary.allPriced && ' *'}</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{formatMoney(summary.currentValue)}</p>
                    </div>
                    <div className="bg-white dark:bg-gray-900/50 rounded-xl p-3">
                      <p className="text-xs text-gray-500">{t('portfolio.summary.gainLoss')}</p>
                      <p className={`text-lg font-bold flex items-center gap-1 ${summary.gainLoss >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {summary.gainLoss >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                        {formatMoney(summary.gainLoss)} ({summary.gainLossPct.toFixed(1)}%)
                      </p>
                    </div>
                  </div>
                )}
                {selectedStocks.length > 0 && !summary.allPriced && (
                  <p className="text-xs text-gray-500">{t('portfolio.summary.priceEstimateNote')}</p>
                )}

                {selectedStocks.length === 0 && !loading.stocks ? (
                  <p className="text-sm text-gray-500 py-4">{t('portfolio.noHoldings')}</p>
                ) : (
                  <div className="space-y-2">
                    {selectedStocks.map((holding) => {
                      const livePrice = priceByTicker[holding.ticker];
                      const qty = Number(holding.quantity) || 0;
                      const cost = Number(holding.purchase_price) || 0;
                      const gain = livePrice != null ? (livePrice - cost) * qty : null;
                      return (
                        <div
                          key={holding.id}
                          className="flex items-center gap-3 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 hover:border-gray-300 dark:border-slate-800/60 dark:hover:border-slate-700/80 rounded-xl p-3 transition-colors"
                        >
                          <StockLogo symbol={holding.ticker} size="w-9 h-9" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">{holding.ticker}</p>
                            <p className="text-xs text-gray-500">{t('portfolio.sharesAtCost', { qty, cost: formatMoney(cost) })}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{livePrice != null ? formatMoney(livePrice) : '—'}</p>
                            <p className={`text-sm font-semibold ${gain == null ? 'text-gray-500' : gain >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                              {gain == null ? '—' : formatMoney(gain)}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteHolding(holding.id)}
                            aria-label={t('portfolio.removeHoldingAriaLabel', { ticker: holding.ticker })}
                            className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 flex-shrink-0"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPage;
